import React from 'react';

import { Space, type TableColumnsType } from 'antd-v5';
import MemberTypeTag from 'scouting-ui/lib/Tags/MemberTypeTag';

import { MemberType } from '@modules/roster-lite/constants';
import * as RosterLiteMember from '@modules/roster-lite/models/rosterLiteMember';
import MultiValueLabel from '@shared/components/MultiValueLabel';
import PersonAvatar from '@shared/components/PersonAvatar/PersonAvatar';
import { intl } from '@shared/localization';

interface FilterOption {
  text: string;
  value: string;
}

export function getColumns(
  positionFilters: FilterOption[],
  isMobile: boolean,
): TableColumnsType<RosterLiteMember.RosterLiteMember> {
  return [
    {
      title: intl.formatMessage({ id: 'shared.name' }),
      key: 'name',
      width: isMobile ? 120 : 220,
      minWidth: isMobile ? 100 : 160,
      fixed: 'left',
      render: (_: unknown, record) => (
        <Space size="small" align="center">
          <PersonAvatar
            size={'md'}
            isAdult={record.memberType === MemberType.Adult}
            gender={record.gender}
            pictureUrl={record.pictureUrl ?? undefined}
          />
          {RosterLiteMember.getDisplayName(record)}
        </Space>
      ),
      sorter: (a, b) =>
        RosterLiteMember.getFullName(a).localeCompare(
          RosterLiteMember.getFullName(b),
        ),
      defaultSortOrder: 'ascend',
    },
    {
      title: intl.formatMessage({ id: 'shared.memberId' }),
      dataIndex: 'memberId',
      key: 'memberId',
      width: 100,
      minWidth: 100,
      sorter: (a, b) =>
        a.memberId.localeCompare(b.memberId, undefined, { numeric: true }),
    },
    {
      title: intl.formatMessage({ id: 'shared.type' }),
      dataIndex: 'memberType',
      key: 'memberType',
      width: '15ch',
      minWidth: 40,
      filters: [
        {
          text: intl.formatMessage({ id: 'shared.youth' }),
          value: MemberType.Youth,
        },
        {
          text: intl.formatMessage({ id: 'shared.adult' }),
          value: MemberType.Adult,
        },
      ],
      onFilter: (value, record) => record.memberType === value,
      sorter: (a, b) => a.memberType.localeCompare(b.memberType),
      render: (value: MemberType) => <MemberTypeTag memberType={value} />,
    },
    {
      title: intl.formatMessage({ id: 'shared.position' }),
      dataIndex: 'positions',
      key: 'positions',
      width: 280,
      filters: positionFilters,
      filterSearch: true,
      onFilter: (value, record) => record.positions.includes(String(value)),
      sorter: (a, b) =>
        (a.positions[0] ?? '').localeCompare(b.positions[0] ?? ''),
      render: (values: string[]) => <MultiValueLabel values={values} />,
    },
    {
      title: intl.formatMessage({ id: 'rosterLite.table.registrationStatus' }),
      dataIndex: 'registrationStatus',
      key: 'registrationStatus',
      width: 180,
      hidden: true,
      sorter: (a, b) =>
        (a.registrationStatus ?? '').localeCompare(b.registrationStatus ?? ''),
    },
    {
      title: intl.formatMessage({ id: 'rosterLite.table.renewalStatus' }),
      dataIndex: 'renewalStatus',
      key: 'renewalStatus',
      width: 180,
      hidden: true,
      sorter: (a, b) =>
        (a.renewalStatus ?? '').localeCompare(b.renewalStatus ?? ''),
    },
  ];
}
